// Calendar Date Cell Component (VAC-8)
// Individual date cell with vacation indicators

import React from 'react'
import { isToday, isSameDay } from 'date-fns'
import type { CalendarEntry } from '@/types/vacation'

interface CalendarDateCellProps {
  date: Date
  vacations: CalendarEntry[]
  isCurrentMonth: boolean
  onClick?: (date: Date) => void
}

export const CalendarDateCell: React.FC<CalendarDateCellProps> = ({
  date,
  vacations,
  isCurrentMonth,
  onClick
}) => {
  const today = isToday(date)
  const hasVacations = vacations.length > 0

  const handleClick = () => {
    if (onClick) {
      onClick(date)
    }
  }

  return (
    <button
      onClick={handleClick}
      className={`
        relative min-h-[80px] md:min-h-[100px] p-2 border border-gray-200 dark:border-gray-700
        transition-colors rounded-lg
        ${isCurrentMonth
          ? 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-750'
          : 'bg-gray-50 dark:bg-gray-900 text-gray-400 dark:text-gray-600'
        }
        ${today ? 'ring-2 ring-blue-500 dark:ring-blue-400' : ''}
        ${hasVacations ? 'has-vacation' : ''}
      `}
      aria-label={`${date.getDate()} ${hasVacations ? `- ${vacations.length} férias` : ''}`}
      aria-current={today ? 'date' : undefined}
    >
      {/* Date Number */}
      <div
        className={`
          text-sm font-semibold mb-1
          ${today
            ? 'text-blue-600 dark:text-blue-400'
            : isCurrentMonth
            ? 'text-gray-900 dark:text-white'
            : 'text-gray-400 dark:text-gray-600'
          }
        `}
      >
        {date.getDate()}
      </div>

      {/* Vacation Indicators */}
      {hasVacations && (
        <div className="space-y-1">
          {vacations.slice(0, 3).map((vacation, index) => {
            const colorClass = vacation.status === 'APPROVED'
              ? 'bg-green-500 dark:bg-green-600'
              : 'bg-yellow-500 dark:bg-yellow-600'

            return (
              <div
                key={`${vacation.requestId}-${index}`}
                className={`h-1.5 rounded-full ${colorClass}`}
                title={`${vacation.employeeName} - ${vacation.status === 'APPROVED' ? 'Aprovada' : 'Pendente'}`}
              />
            )
          })}
          {vacations.length > 3 && (
            <div className="text-xs text-gray-600 dark:text-gray-400 text-center">
              +{vacations.length - 3}
            </div>
          )}
        </div>
      )}

      {/* Tooltip on Hover (CSS-based) */}
      {hasVacations && (
        <div className="absolute hidden group-hover:block bottom-full left-1/2 transform -translate-x-1/2 mb-2 p-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded shadow-lg z-10 whitespace-nowrap">
          {vacations.map((vacation, index) => (
            <div key={`${vacation.requestId}-tooltip-${index}`}>
              {vacation.employeeName} ({vacation.totalDays}d)
            </div>
          ))}
        </div>
      )}
    </button>
  )
}
